<mon-page-header [monIcon]="['ACTIONS', 'DELETE']" header="{{'Delete Users' | translate}}">
</mon-page-header>

<mon-panel [showHeader]="false">
    <div class="card-body" slot="mon-panel-body">
        
        <!-- Step 1: Email Input -->
        <div *ngIf="currentStep === 'input'" class="delete-users-input">
            <div class="mb-3">
                <label for="emailInput" class="form-label">{{'Paste text containing email addresses' | translate}}</label>
                <textarea 
                    id="emailInput"
                    class="form-control" 
                    rows="10" 
                    [(ngModel)]="emailInput"
                    placeholder="{{'Paste any text containing email addresses here' | translate}}">
                </textarea>
                <small class="form-text text-muted" translate>
                    Paste any text containing email addresses. All email addresses will be automatically extracted and searched.
                </small>
            </div>
            <button 
                class="btn btn-primary" 
                (click)="extractEmails()"
                [disabled]="!emailInput || emailInput.trim().length === 0"
                translate>
                Extract Emails
            </button>
        </div>

        <!-- Step 2: Confirm Email Addresses -->
        <div *ngIf="currentStep === 'confirm-emails'" class="delete-users-confirm">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 translate>Confirm Email Addresses</h4>
                <button class="btn btn-secondary" (click)="goBackToInput()" translate>
                    Back to Input
                </button>
            </div>
            
            <div class="alert alert-info">
                <span translate>Found</span> {{extractedEmails.length}} <span translate>email addresses. Please confirm before searching:</span>
            </div>
            
            <div class="mb-3">
                <ul class="list-group">
                    <li *ngFor="let email of extractedEmails" class="list-group-item">
                        {{email}}
                    </li>
                </ul>
            </div>
            
            <div class="d-flex gap-2">
                <button 
                    class="btn btn-primary" 
                    (click)="confirmAndSearch()"
                    [disabled]="extractedEmails.length === 0"
                    translate>
                    Search Users
                </button>
                <button class="btn btn-secondary" (click)="goBackToInput()" translate>
                    Edit Input
                </button>
            </div>
        </div>

        <!-- Step 3: Searching in Progress -->
        <div *ngIf="currentStep === 'searching'" class="delete-users-searching">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 translate>Searching Across Environments</h4>
                <button class="btn btn-secondary" (click)="goBackToEmailConfirmation()" [disabled]="loading" translate>
                    Back to Email List
                </button>
            </div>
            
            <div class="mb-3">
                <div class="alert alert-info">
                    <span translate>Searching</span> {{extractedEmails.length}} <span translate>emails across</span> {{getTotalEnvironments()}} <span translate>environments...</span>
                    <br>
                    <strong>{{getSearchProgressPercentage()}}%</strong> <span translate>complete</span>
                </div>
                <div class="progress">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" [style.width.%]="getSearchProgressPercentage()"></div>
                </div>
            </div>
        </div>

        <!-- Step 4: Search Results -->
        <div *ngIf="currentStep === 'results'" class="delete-users-results">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 translate>Search Results</h4>
                <button class="btn btn-secondary" (click)="goBackToEmailConfirmation()" translate>
                    Back to Email List
                </button>
            </div>
            
            <mon-table-container
                [collection]="{data: searchResults, totalRows: searchResults.length}"
                class="table-vertial-align">
                <div class="table-container-top">
                    <div class="alert alert-info">
                        <span translate>Found</span> {{getTotalUsersFound()}} <span translate>users across all environments</span>
                    </div>
                    <button 
                        class="btn btn-danger"
                        [disabled]="getTotalUsersFound() === 0"
                        monConfirm="{{'Are you sure you want to proceed to delete all found users? This action cannot be undone.' | translate}}"
                        (monConfirmAction)="confirmDeletion()"
                        translate>
                        Proceed to Delete
                    </button>
                </div>
                <div class="table-container-body">
                    <div *ngIf="searchResults && searchResults.length > 0" class="table-responsive">
                        
                        <!-- Results by Email (only show emails with users found) -->
                        <div *ngFor="let emailResult of getEmailsWithUsers()" class="mb-4">
                            <h5 class="mb-3">
                                <strong>{{emailResult.email}}</strong> 
                                <span class="badge badge-primary ms-2">{{emailResult.totalUsers}} users found</span>
                            </h5>
                            
                            <!-- Environment breakdown for this email (only show environments with users) -->
                            <div *ngFor="let env of getEnvironmentsWithUsers(emailResult)" class="mb-2">
                                <div class="card">
                                    <div class="card-header d-flex justify-content-between align-items-center">
                                        <strong>{{env.name}}</strong>
                                        <span class="badge badge-success">
                                            {{env.users.length}} users
                                        </span>
                                    </div>
                                    <div class="card-body">
                                        <table class="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th translate>User ID</th>
                                                    <th translate>Email</th>
                                                    <th translate>Name</th>
                                                    <th translate>Customers</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr *ngFor="let user of env.users">
                                                    <td>{{user.id}}</td>
                                                    <td>{{user.email}}</td>
                                                    <td>{{getUserDisplayName(user)}}</td>
                                                    <td>
                                                        <div *ngIf="user.user_agreements && user.user_agreements.length > 0">
                                                            <div *ngFor="let agreement of user.user_agreements" class="mb-1">
                                                                <span>
                                                                    {{agreement.account?.name || 'N/A'}}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <span *ngIf="!user.user_agreements || user.user_agreements.length === 0" class="text-muted">
                                                            No customer associations
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Combined list of emails with no users found -->
                        <div *ngIf="getEmailsWithoutUsers().length > 0" class="mb-4">
                            <h5 class="mb-3 text-muted">
                                <strong translate>Emails Not Found</strong>
                                <span class="badge badge-warning ms-2">{{getEmailsWithoutUsers().length}} emails</span>
                            </h5>
                            <div class="card">
                                <div class="card-header">
                                    <strong translate>No users found for these email addresses</strong>
                                </div>
                                <div class="card-body">
                                    <ul class="list-unstyled mb-0">
                                        <li *ngFor="let email of getEmailsWithoutUsers()" class="mb-1">
                                            <span class="text-muted">{{email}}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div *ngIf="!searchResults || searchResults.length === 0" class="text-center p-4">
                        <span translate>No search results</span>
                    </div>
                </div>
            </mon-table-container>
        </div>

        <!-- Step 5: Deletion in Progress -->
        <div *ngIf="currentStep === 'deleting'" class="delete-users-deleting">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 translate>Deleting Users</h4>
                <button class="btn btn-secondary" (click)="goBackToResults()" [disabled]="isDeleting" translate>
                    Back to Results
                </button>
            </div>
            
            <div class="alert alert-warning">
                <h5 translate>Deletion in Progress</h5>
                <p translate>Please wait while we delete all found users from their respective environments...</p>
                <div class="d-flex align-items-center">
                    <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                    <span translate>Deleting users...</span>
                </div>
            </div>

            <div class="alert alert-info">
                <span translate>Deleting</span> {{getTotalUsersFound()}} <span translate>users across all environments</span>
            </div>
        </div>

        <!-- Step 6: Success Confirmation -->
        <div *ngIf="currentStep === 'success'" class="delete-users-success">
            <div class="alert alert-success">
                <h4 translate>Users Deleted Successfully</h4>
                <p translate>All selected users have been successfully deleted from all environments.</p>
            </div>
            <button class="btn btn-primary" (click)="startOver()" translate>
                Delete More Users
            </button>
        </div>
        
    </div>
</mon-panel>